import React, { useEffect, useRef, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightAndDownLeftFromCenter } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Extension } from "@tiptap/core";
import {
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";

import {
  AuthenticatedImageAttributes,
  AuthenticatedImageNode as CoreAuthenticatedImageNode,
} from "@nnr/tiptap-extensions";
import { useFileUrlAction } from "../../../action/use-file-url-action";

interface AuthenticatedImageProps extends Omit<NodeViewProps, "node"> {
  node: {
    attrs: AuthenticatedImageAttributes;
  } & NodeViewProps["node"];
  selected: boolean;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    authenticatedImage: {
      setImage: (options: Partial<AuthenticatedImageAttributes>) => ReturnType;
    };
  }
}

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  node,
  selected,
  updateAttributes,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [dimensions, setDimensions] = useState<{
    width: string;
    height: string;
  }>({
    width: node.attrs.width || "100%",
    height: node.attrs.height || "auto",
  });
  const { getFileUrl } = useFileUrlAction();

  const imageRef = useRef<HTMLImageElement>(null);
  const initialDimensionsRef = useRef<{
    width: number;
    height: number;
    aspectRatio: number;
  }>({ width: 0, height: 0, aspectRatio: 1 });
  const initialMousePosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const fileUuid = node.attrs.src;
  const folderUuid = node.attrs.folderUuid;

  useEffect(() => {
    if (!fileUuid) {
      setError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);

    getFileUrl(fileUuid)
      .then((url) => {
        if (!url) {
          throw new Error("File URL not found");
        }
        setImageSrc(url);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading image:", err);
        setError(true);
        setLoading(false);
      });
  }, [fileUuid]);

  // Setup resize handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      e.preventDefault();

      const dx = e.clientX - initialMousePosRef.current.x;
      const newWidth = initialDimensionsRef.current.width + dx;

      // Calculate height based on aspect ratio
      const newHeight = newWidth / initialDimensionsRef.current.aspectRatio;

      // Update dimensions
      setDimensions({
        width: `${newWidth}px`,
        height: `${newHeight}px`,
      });
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);

        // Save dimensions to node attributes
        updateAttributes({
          width: dimensions.width,
          height: dimensions.height,
        });
      }
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, updateAttributes, dimensions]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      initialDimensionsRef.current = {
        width: rect.width,
        height: rect.height,
        aspectRatio: rect.width / rect.height,
      };
      initialMousePosRef.current = {
        x: e.clientX,
        y: e.clientY,
      };

      setIsResizing(true);
    }
  };

  if (loading) {
    return (
      <NodeViewWrapper>
        <div className="image-loading bg-background-2 flex h-12 items-center justify-center rounded">
          Loading...
        </div>
      </NodeViewWrapper>
    );
  }

  if (error) {
    return (
      <NodeViewWrapper>
        <div className="image-error flex h-12 items-center justify-center rounded bg-red-50 text-red-600">
          Error loading image
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper>
      <div
        className="relative"
        style={{ width: dimensions.width, height: dimensions.height }}
      >
        <img
          ref={imageRef}
          src={imageSrc ?? undefined}
          alt={node.attrs.alt ?? undefined}
          title={node.attrs.title ?? undefined}
          className="rounded-lg"
          style={{
            boxSizing: "border-box",
            border: selected ? "2px solid black" : "2px solid transparent",
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
          data-file-uuid={fileUuid}
          data-folder-uuid={folderUuid}
          data-drag-handle
          draggable="true"
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
          }}
        />
        {selected && (
          <div
            onMouseDown={handleResizeStart}
            className="bg-foreground absolute right-2 bottom-2 z-10 flex h-4 w-4 rotate-90 cursor-nwse-resize items-center justify-center rounded text-white"
          >
            <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} size="xs" />
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
};

export const AuthenticatedImageNodeWithView =
  CoreAuthenticatedImageNode.extend<Extension>({
    addNodeView() {
      return ReactNodeViewRenderer(
        // @ts-ignore React type compatibility varies between local and CI environments
        AuthenticatedImage as React.FC<NodeViewProps>,
      );
    },
  });
